/*
 * © 2026 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConnectionTimeoutError = exports.DataRenderingError = exports.GeneralSecretsFailureError = exports.GenericNetworkError = exports.ConnectionRefusedError = exports.TLSCertificateError = exports.NetworkUnreachableError = exports.NetworkTimeoutError = exports.DNSResolutionError = exports.FeatureNotEnabledError = exports.CommandIsExperimentalError = exports.FeatureUnderDevelopmentError = exports.GeneralSASTFailureError = exports.GeneralIACFailureError = exports.GeneralSCAFailureError = exports.ValidationFailureError = exports.TooManyVulnerablePathsError = exports.NoSupportedFilesFoundError = exports.CommandArgsError = exports.AuthConfigError = exports.GetVulnsFromResourceFailedError = exports.InvalidFlagOptionError = exports.EmptyFlagOptionError = exports.ConfigEnvironmentConsistencyIssueError = exports.ConfigEnvironmentFailedError = exports.GeneralCLIFailureError = void 0;
const types_1 = require("../types");
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name GeneralCLIFailureError
 * @description The encountered error only provides basic information, please take a look at the given details. If they do not help to resolve the issue, consider debugging or consulting support.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands](https://docs.snyk.io/snyk-cli/commands)
 * - [https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli](https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli)
 * @summary Unspecified Error
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GeneralCLIFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unspecified Error',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0000',
            status: 0,
            description: `The encountered error only provides basic information, please take a look at the given details. If they do not help to resolve the issue, consider debugging or consulting support.`,
            errorCode: 'SNYK-CLI-0000',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands',
                'https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GeneralCLIFailureError = GeneralCLIFailureError;
/**
 * @class
 * @name ConfigEnvironmentFailedError
 * @description The specified environment cannot be used. As a result, the configuration remains unchanged. Provide the correct specifications for the environment and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/config-environment](https://docs.snyk.io/snyk-cli/commands/config-environment)
 * @summary Unable to set environment
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConfigEnvironmentFailedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to set environment',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0001',
            status: 0,
            description: `The specified environment cannot be used. As a result, the configuration remains unchanged. Provide the correct specifications for the environment and try again.`,
            errorCode: 'SNYK-CLI-0001',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/config-environment',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConfigEnvironmentFailedError = ConfigEnvironmentFailedError;
/**
 * @class
 * @name ConfigEnvironmentConsistencyIssueError
 * @description You can configure the CLI in different ways, for example via Environment Variables or configuration file.
 * If one parameter is configured multiple times, it is probably unintentional and might cause unexpected behavior.
 * Review configured environment variables and ensure that everything is intentional. If so, you can skip this check by using --no-check.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/config-environment](https://docs.snyk.io/snyk-cli/commands/config-environment)
 * @summary Possible inconsistent configuration
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConfigEnvironmentConsistencyIssueError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Possible inconsistent configuration',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0002',
            status: 0,
            description: `You can configure the CLI in different ways, for example via Environment Variables or configuration file.
If one parameter is configured multiple times, it is probably unintentional and might cause unexpected behavior.
Review configured environment variables and ensure that everything is intentional. If so, you can skip this check by using --no-check.`,
            errorCode: 'SNYK-CLI-0002',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/config-environment',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConfigEnvironmentConsistencyIssueError = ConfigEnvironmentConsistencyIssueError;
/**
 * @class
 * @name EmptyFlagOptionError
 * @description A specified flag is missing an option value. Provide a correct option value and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary](https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary)
 * @summary Empty flag option
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class EmptyFlagOptionError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Empty flag option',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0003',
            status: 0,
            description: `A specified flag is missing an option value. Provide a correct option value and try again.`,
            errorCode: 'SNYK-CLI-0003',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.EmptyFlagOptionError = EmptyFlagOptionError;
/**
 * @class
 * @name InvalidFlagOptionError
 * @description A specified flag option or combination is invalid. Provide a valid flag option or combination and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary](https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary)
 * @summary Invalid flag option
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class InvalidFlagOptionError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Invalid flag option',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0004',
            status: 0,
            description: `A specified flag option or combination is invalid. Provide a valid flag option or combination and try again.`,
            errorCode: 'SNYK-CLI-0004',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.InvalidFlagOptionError = InvalidFlagOptionError;
/**
 * @class
 * @name GetVulnsFromResourceFailedError
 * @description If you are testing an npm package, check the version and package name and try running `snyk test` again. If you are testing a repository, try testing it at https://snyk.io/test/. For further assistance, run `snyk help` or see the Snyk docs.
 * @summary Unable to get vulnerabilities from resource
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GetVulnsFromResourceFailedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Unable to get vulnerabilities from resource',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0005',
            status: 0,
            description: `If you are testing an npm package, check the version and package name and try running \`snyk test\` again. If you are testing a repository, try testing it at https://snyk.io/test/. For further assistance, run \`snyk help\` or see the Snyk docs.`,
            errorCode: 'SNYK-CLI-0005',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GetVulnsFromResourceFailedError = GetVulnsFromResourceFailedError;
/**
 * @class
 * @name AuthConfigError
 * @description When running your command, Snyk requires an authenticated account. You must include your API token as an environment value, or use `snyk auth` to authenticate.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/authenticate-to-use-the-cli](https://docs.snyk.io/snyk-cli/authenticate-to-use-the-cli)
 * - [https://docs.snyk.io/snyk-cli/configure-the-snyk-cli/environment-variables-for-snyk-cli](https://docs.snyk.io/snyk-cli/configure-the-snyk-cli/environment-variables-for-snyk-cli)
 * @summary Missing AUTH token
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class AuthConfigError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Missing AUTH token',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0006',
            status: 0,
            description: `When running your command, Snyk requires an authenticated account. You must include your API token as an environment value, or use \`snyk auth\` to authenticate.`,
            errorCode: 'SNYK-CLI-0006',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/authenticate-to-use-the-cli',
                'https://docs.snyk.io/snyk-cli/configure-the-snyk-cli/environment-variables-for-snyk-cli',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.AuthConfigError = AuthConfigError;
/**
 * @class
 * @name CommandArgsError
 * @description The specified CLI command includes missing or misconfigured arguments. Provide the correct arguments and try again.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary](https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary)
 * @summary Incomplete command arguments
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class CommandArgsError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Incomplete command arguments',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0007',
            status: 0,
            description: `The specified CLI command includes missing or misconfigured arguments. Provide the correct arguments and try again.`,
            errorCode: 'SNYK-CLI-0007',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.CommandArgsError = CommandArgsError;
/**
 * @class
 * @name NoSupportedFilesFoundError
 * @description Snyk could not detect any supported target files. Ensure the files you are importing are supported, that you are in the right directory, and try again.
 *
 * See more:
 * - [https://docs.snyk.io/supported-languages-package-managers-and-frameworks](https://docs.snyk.io/supported-languages-package-managers-and-frameworks)
 * @summary No supported files found
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class NoSupportedFilesFoundError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'No supported files found',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0008',
            status: 0,
            description: `Snyk could not detect any supported target files. Ensure the files you are importing are supported, that you are in the right directory, and try again.`,
            errorCode: 'SNYK-CLI-0008',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/supported-languages-package-managers-and-frameworks',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.NoSupportedFilesFoundError = NoSupportedFilesFoundError;
/**
 * @class
 * @name TooManyVulnerablePathsError
 * @description There are too many vulnerable paths to process the project. If your command supports it, consider the following: pruning repeated sub-dependencies (`snyk test -p`); excluding directories (`snyk test --all-projects --exclude=dir1,file2`); setting a detection depth (`snyk test --all-projects --detection-depth=3`). If the error still occurs, consider debugging or contact Snyk Support.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary#options-for-multiple-commands](https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary#options-for-multiple-commands)
 * - [https://docs.snyk.io/snyk-cli/commands/test#prune-repeated-subdependencies-p](https://docs.snyk.io/snyk-cli/commands/test#prune-repeated-subdependencies-p)
 * - [https://docs.snyk.io/snyk-cli/commands/test#detection-depth-less-than-depth-greater-than](https://docs.snyk.io/snyk-cli/commands/test#detection-depth-less-than-depth-greater-than)
 * - [https://docs.snyk.io/snyk-cli/commands/test#exclude-less-than-name-greater-than-less-than-name-greater-than-...greater-than](https://docs.snyk.io/snyk-cli/commands/test#exclude-less-than-name-greater-than-less-than-name-greater-than-...greater-than)
 * @summary Too many vulnerable paths to Project
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TooManyVulnerablePathsError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Too many vulnerable paths to Project',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0009',
            status: 413,
            description: `There are too many vulnerable paths to process the project. If your command supports it, consider the following: pruning repeated sub-dependencies (\`snyk test -p\`); excluding directories (\`snyk test --all-projects --exclude=dir1,file2\`); setting a detection depth (\`snyk test --all-projects --detection-depth=3\`). If the error still occurs, consider debugging or contact Snyk Support.`,
            errorCode: 'SNYK-CLI-0009',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/cli-commands-and-options-summary#options-for-multiple-commands',
                'https://docs.snyk.io/snyk-cli/commands/test#prune-repeated-subdependencies-p',
                'https://docs.snyk.io/snyk-cli/commands/test#detection-depth-less-than-depth-greater-than',
                'https://docs.snyk.io/snyk-cli/commands/test#exclude-less-than-name-greater-than-less-than-name-greater-than-...greater-than',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TooManyVulnerablePathsError = TooManyVulnerablePathsError;
/**
 * @class
 * @name ValidationFailureError
 * @description CLI was unable to validate the required parameter. Provide the correct parameter and try again. If the error still occurs, consider debugging or contact Snyk Support.
 * @summary CLI validation failure
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ValidationFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'CLI validation failure',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0010',
            status: 400,
            description: `CLI was unable to validate the required parameter. Provide the correct parameter and try again. If the error still occurs, consider debugging or contact Snyk Support.`,
            errorCode: 'SNYK-CLI-0010',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ValidationFailureError = ValidationFailureError;
/**
 * @class
 * @name GeneralSCAFailureError
 * @description CLI was unable to execute your SCA command, please take a look at the given details. If they do not help to resolve the issue, consider debugging or consulting support.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/test](https://docs.snyk.io/snyk-cli/commands/test)
 * @summary SCA failure
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GeneralSCAFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'SCA failure',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0011',
            status: 0,
            description: `CLI was unable to execute your SCA command, please take a look at the given details. If they do not help to resolve the issue, consider debugging or consulting support.`,
            errorCode: 'SNYK-CLI-0011',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/test',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GeneralSCAFailureError = GeneralSCAFailureError;
/**
 * @class
 * @name GeneralIACFailureError
 * @description CLI was unable to execute your IAC command, please take a look at the given details. If they do not help to resolve the issue, consider debugging or consulting support.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/iac](https://docs.snyk.io/snyk-cli/commands/iac)
 * @summary IAC failure
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GeneralIACFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'IAC failure',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0012',
            status: 0,
            description: `CLI was unable to execute your IAC command, please take a look at the given details. If they do not help to resolve the issue, consider debugging or consulting support.`,
            errorCode: 'SNYK-CLI-0012',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/iac',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GeneralIACFailureError = GeneralIACFailureError;
/**
 * @class
 * @name GeneralSASTFailureError
 * @description CLI was unable to execute your SAST command, please take a look at the given details. If they do not help to resolve the issue, consider debugging or consulting support.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/commands/code](https://docs.snyk.io/snyk-cli/commands/code)
 * @summary SAST failure
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GeneralSASTFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'SAST failure',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0013',
            status: 0,
            description: `CLI was unable to execute your SAST command, please take a look at the given details. If they do not help to resolve the issue, consider debugging or consulting support.`,
            errorCode: 'SNYK-CLI-0013',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/commands/code',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GeneralSASTFailureError = GeneralSASTFailureError;
/**
 * @class
 * @name FeatureUnderDevelopmentError
 * @description This feature is under development and is not yet available for public use.
 * @summary Feature under development
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FeatureUnderDevelopmentError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Feature under development',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0014',
            status: 0,
            description: `This feature is under development and is not yet available for public use.`,
            errorCode: 'SNYK-CLI-0014',
            level: 'fatal',
            classification: types_1.Classification.UNSUPPORTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.FeatureUnderDevelopmentError = FeatureUnderDevelopmentError;
/**
 * @class
 * @name CommandIsExperimentalError
 * @description This CLI command is experimental, which means it is provided "as-is" without warranty of any kind.
 * You must acknowledge this by specifying the --experimental flag to run the command.
 * @summary Command is experimental
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class CommandIsExperimentalError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Command is experimental',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0015',
            status: 0,
            description: `This CLI command is experimental, which means it is provided "as-is" without warranty of any kind.
You must acknowledge this by specifying the --experimental flag to run the command.`,
            errorCode: 'SNYK-CLI-0015',
            level: 'fatal',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.CommandIsExperimentalError = CommandIsExperimentalError;
/**
 * @class
 * @name FeatureNotEnabledError
 * @description This feature is disabled for your current organization. You can enable it in the settings or switch to an organization where it's already enabled.
 * @summary Feature not enabled
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FeatureNotEnabledError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Feature not enabled',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0016',
            status: 403,
            description: `This feature is disabled for your current organization. You can enable it in the settings or switch to an organization where it's already enabled.`,
            errorCode: 'SNYK-CLI-0016',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.FeatureNotEnabledError = FeatureNotEnabledError;
/**
 * @class
 * @name DNSResolutionError
 * @description Unable to resolve the hostname to an IP address. Troubleshooting steps.
 * 1) Test DNS resolution: nslookup api.<instance>.snyk.io.
 * 2) Try different DNS servers: Change DNS to 8.8.8.8 or 1.1.1.1.
 * 3) Check corporate proxy/firewall DNS blocking.
 * 4) Verify hostname spelling in your Snyk configuration.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli](https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli)
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary DNS resolution failed
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class DNSResolutionError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'DNS resolution failed',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0017',
            status: 0,
            description: `Unable to resolve the hostname to an IP address. Troubleshooting steps.
1) Test DNS resolution: nslookup api.<instance>.snyk.io.
2) Try different DNS servers: Change DNS to 8.8.8.8 or 1.1.1.1.
3) Check corporate proxy/firewall DNS blocking.
4) Verify hostname spelling in your Snyk configuration.`,
            errorCode: 'SNYK-CLI-0017',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli',
                'https://status.snyk.io/',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.DNSResolutionError = DNSResolutionError;
/**
 * @class
 * @name NetworkTimeoutError
 * @description The network request timed out. Troubleshooting steps.
 * 1) Test connectivity: ping api.<instance>.snyk.io.
 * 2) Check corporate proxy timeout settings.
 * 3) Try different network: Mobile hotspot or different WiFi.
 * 4) Check if firewall is blocking or throttling connections.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * - [https://privatecloudstatus.snyk.io](https://privatecloudstatus.snyk.io)
 * - [https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli](https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli)
 * @summary Network request timeout
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class NetworkTimeoutError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Network request timeout',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0018',
            status: 0,
            description: `The network request timed out. Troubleshooting steps.
1) Test connectivity: ping api.<instance>.snyk.io.
2) Check corporate proxy timeout settings.
3) Try different network: Mobile hotspot or different WiFi.
4) Check if firewall is blocking or throttling connections.`,
            errorCode: 'SNYK-CLI-0018',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
                'https://privatecloudstatus.snyk.io',
                'https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.NetworkTimeoutError = NetworkTimeoutError;
/**
 * @class
 * @name NetworkUnreachableError
 * @description Unable to reach the target network or host. Troubleshooting steps.
 * 1) Test Snyk connectivity: ping api.<instance>.snyk.io.
 * 2) Check corporate firewall blocks Snyk domains.
 * 3) Check if VPN routing is blocking Snyk domains.
 * 4) Try mobile hotspot to isolate network issues.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli](https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli)
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary Network unreachable
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class NetworkUnreachableError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Network unreachable',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0019',
            status: 0,
            description: `Unable to reach the target network or host. Troubleshooting steps.
1) Test Snyk connectivity: ping api.<instance>.snyk.io.
2) Check corporate firewall blocks Snyk domains.
3) Check if VPN routing is blocking Snyk domains.
4) Try mobile hotspot to isolate network issues.`,
            errorCode: 'SNYK-CLI-0019',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli',
                'https://status.snyk.io/',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.NetworkUnreachableError = NetworkUnreachableError;
/**
 * @class
 * @name TLSCertificateError
 * @description There was an issue with the TLS/SSL certificate during the secure connection. Troubleshooting steps.
 * 1) Check system time: Ensure your system clock is correct (certificates are time-sensitive).
 * 2) Update system certificates: Windows Update or macOS Software Update.
 * 3) Corporate firewall: Check if corporate firewall intercepts SSL traffic.
 * 4) Custom certificates: Set NODE_EXTRA_CA_CERTS environment variable to path of your CA certificate file.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli](https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli)
 * - [https://docs.snyk.io/snyk-cli/configure-the-snyk-cli/environment-variables-for-snyk-cli](https://docs.snyk.io/snyk-cli/configure-the-snyk-cli/environment-variables-for-snyk-cli)
 * @summary TLS certificate error
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TLSCertificateError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'TLS certificate error',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0020',
            status: 0,
            description: `There was an issue with the TLS/SSL certificate during the secure connection. Troubleshooting steps.
1) Check system time: Ensure your system clock is correct (certificates are time-sensitive).
2) Update system certificates: Windows Update or macOS Software Update.
3) Corporate firewall: Check if corporate firewall intercepts SSL traffic.
4) Custom certificates: Set NODE_EXTRA_CA_CERTS environment variable to path of your CA certificate file.`,
            errorCode: 'SNYK-CLI-0020',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli',
                'https://docs.snyk.io/snyk-cli/configure-the-snyk-cli/environment-variables-for-snyk-cli',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TLSCertificateError = TLSCertificateError;
/**
 * @class
 * @name ConnectionRefusedError
 * @description The connection to the server was refused. Troubleshooting steps.
 * 1) Check Snyk status: Visit status.snyk.io for service outages.
 * 2) Test connectivity: ping api.<instance>.snyk.io.
 * 3) Check corporate proxy blocks HTTPS connections to Snyk.
 * 4) Try mobile hotspot or different network.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * - [https://privatecloudstatus.snyk.io](https://privatecloudstatus.snyk.io)
 * - [https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli](https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli)
 * @summary Connection refused
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConnectionRefusedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Connection refused',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0021',
            status: 0,
            description: `The connection to the server was refused. Troubleshooting steps.
1) Check Snyk status: Visit status.snyk.io for service outages.
2) Test connectivity: ping api.<instance>.snyk.io.
3) Check corporate proxy blocks HTTPS connections to Snyk.
4) Try mobile hotspot or different network.`,
            errorCode: 'SNYK-CLI-0021',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
                'https://privatecloudstatus.snyk.io',
                'https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConnectionRefusedError = ConnectionRefusedError;
/**
 * @class
 * @name GenericNetworkError
 * @description An unexpected network error occurred during communication. Troubleshooting steps.
 * 1) Test connectivity: ping api.<instance>.snyk.io.
 * 2) Check proxy settings: HTTP_PROXY and HTTPS_PROXY environment variables.
 * 3) Run with verbose logging: snyk command --debug.
 * 4) Try mobile hotspot to isolate network issues.
 *
 * See more:
 * - [https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli](https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli)
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * @summary Network communication error
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GenericNetworkError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Network communication error',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0022',
            status: 0,
            description: `An unexpected network error occurred during communication. Troubleshooting steps.
1) Test connectivity: ping api.<instance>.snyk.io.
2) Check proxy settings: HTTP_PROXY and HTTPS_PROXY environment variables.
3) Run with verbose logging: snyk command --debug.
4) Try mobile hotspot to isolate network issues.`,
            errorCode: 'SNYK-CLI-0022',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [
                'https://docs.snyk.io/snyk-cli/debugging-the-snyk-cli',
                'https://status.snyk.io/',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GenericNetworkError = GenericNetworkError;
/**
 * @class
 * @name GeneralSecretsFailureError
 * @description CLI was unable to execute your Secrets command, please take a look at the given details. If they do not help to resolve the issue, consider debugging or consulting support.
 * @summary Secrets failure
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class GeneralSecretsFailureError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Secrets failure',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0023',
            status: 0,
            description: `CLI was unable to execute your Secrets command, please take a look at the given details. If they do not help to resolve the issue, consider debugging or consulting support.`,
            errorCode: 'SNYK-CLI-0023',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.GeneralSecretsFailureError = GeneralSecretsFailureError;
/**
 * @class
 * @name DataRenderingError
 * @description Rendering the data to at least one of the required outputs failed. Please review the error details provided.
 * If the details do not help resolve the issue, consider debugging or contacting support.
 * @summary Data rendering failed
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class DataRenderingError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Data rendering failed',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-cli-0024',
            status: 0,
            description: `Rendering the data to at least one of the required outputs failed. Please review the error details provided.
If the details do not help resolve the issue, consider debugging or contacting support.`,
            errorCode: 'SNYK-CLI-0024',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.DataRenderingError = DataRenderingError;
/**
 * @class
 * @name ConnectionTimeoutError
 * @description A request to the Snyk API has unexpectedly timeout. Check Snyk status, then try again.
 *
 * See more:
 * - [https://status.snyk.io/](https://status.snyk.io/)
 * - [https://privatecloudstatus.snyk.io](https://privatecloudstatus.snyk.io)
 * @summary Request to Snyk API timeout
 * @category CLI
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class ConnectionTimeoutError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Request to Snyk API timeout',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-os-7001',
            status: 504,
            description: `A request to the Snyk API has unexpectedly timeout. Check Snyk status, then try again.`,
            errorCode: 'SNYK-OS-7001',
            level: 'error',
            classification: types_1.Classification.UNEXPECTED,
            instance,
        }, details, Object.assign({ links: [
                'https://status.snyk.io/',
                'https://privatecloudstatus.snyk.io',
            ] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.ConnectionTimeoutError = ConnectionTimeoutError;
//# sourceMappingURL=CLI-error-catalog.js.map